// Puerto de packages/hce/src/hce/QuestionnaireEngine/extractors.ts (Módulo H,
// Grupo 1) — regla híbrida: el QuestionnaireResponse ya es la fuente de
// verdad con fidelidad legal completa; esto solo saca aparte, como recursos
// FHIR discretos, lo que tiene valor de interoperabilidad (diagnóstico
// CIE-10 → Condition, hallazgos → Observation, motivo → Encounter.reasonCode).
//
// Recortado a propósito respecto del original: sin `construirReceta` (005
// tiene su propio wizard con firma entre medio, todavía fuera de alcance —
// `construirConsentimientoInformado`/024 del Grupo 2 SÍ está portada, más
// abajo) — y con `SISTEMA_CIE10` inlineada en vez de importarla de un
// archivo que solo existe en packages/hce.
import type { MedplumClient } from '@medplum/core';
import type { CodeableConcept, DocumentReference, Encounter, Patient, QuestionnaireResponse, Reference } from '@medplum/fhirtypes';
import { LoteTransaccional } from './discretos/transaccionFhir';
import { SISTEMA_SUBTIPO_CONSENTIMIENTO } from '../lib/consentimientos';
import { indexarItems, indexarRespuestas, numeroDe, respuestasCrudasDe, textoDe } from '@logiacare/hce-pdf';
import {
  decisionDe,
  EXT_FIRMA_PACIENTE,
  EXT_FIRMA_REPRESENTANTE,
  EXT_FIRMA_TESTIGO,
  EXT_REPRESENTANTE,
  EXT_TESTIGO,
  extensionPersona,
  firmaPorRepresentante,
  instanteDeLaDecision,
  representanteDe,
  textoPersona,
} from '../lib/consentimiento024';

const SISTEMA_CIE10 = 'http://logiacare.ec/fhir/CodeSystem/cie10-minimo';

/**
 * `Condition.code` a partir de una respuesta CRUDA de un item `diagnostico-*`
 * — el item es `open-choice` con `answerValueSet` apuntando al catálogo
 * CIE-10 mínimo: `valueCoding` = eligió una opción real del catálogo (queda
 * `coding`); `valueString` = texto libre que no matcheó ninguna (sin código).
 */
function codeDeRespuestaDiagnostico(answer: { valueCoding?: { system?: string; code?: string; display?: string }; valueString?: string }): CodeableConcept | null {
  if (answer.valueCoding?.code) {
    return {
      text: answer.valueCoding.display ?? answer.valueCoding.code,
      coding: [{ system: answer.valueCoding.system ?? SISTEMA_CIE10, code: answer.valueCoding.code, display: answer.valueCoding.display }],
    };
  }
  if (answer.valueString?.trim()) return { text: answer.valueString.trim() };
  return null;
}

export interface ExtractorContext {
  medplum: MedplumClient;
  /** Lote donde el extractor AGREGA los recursos derivados; el Motor lo envía junto con el QuestionnaireResponse en UNA transacción. Las lecturas previas (readReference) se hacen dentro del extractor, antes de enviar. */
  lote: LoteTransaccional;
  subject?: Reference<Patient>;
  encounter?: Reference<Encounter>;
}

export type Extractor = (response: QuestionnaireResponse, ctx: ExtractorContext) => void | Promise<void>;

// Ficha del paciente: el campo de alergias es texto libre con linkId
// distinto entre formularios (antecedentes-alergicos en 002/003/008 y los
// que comparten sus linkId; alergias en 018 Pre Anestésico) — respuestas negativas
// reconocibles no generan recurso (evita un AllergyIntolerance vacío de
// valor por cada "No refiere").
const RESPUESTAS_ALERGIA_NEGATIVAS = new Set([
  'no refiere',
  'ninguna',
  'ninguno',
  'no conocidas',
  'no conocida',
  'sin alergias',
  'no tiene',
  'niega',
  'no',
]);

export function esRespuestaAlergiaNegativa(texto: string): boolean {
  return RESPUESTAS_ALERGIA_NEGATIVAS.has(texto.trim().toLowerCase().replace(/[.]+$/, ''));
}

/**
 * `clinicalStatus` "active" (terminología estándar HL7) — invariante FHIR
 * `ait-1` exige que esté presente salvo que `verificationStatus` sea
 * "entered-in-error". Exportado para que AlergiasTab.tsx (alta manual) use
 * exactamente el mismo valor.
 */
export const CLINICAL_STATUS_ALERGIA_ACTIVA = {
  coding: [{ system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical', code: 'active', display: 'Active' }],
};

/**
 * Un `AllergyIntolerance` por respuesta no vacía/no negativa — sin `coding`
 * en `code` (no hay catálogo oficial de alergias verificado todavía). No
 * deduplica contra alergias ya registradas en visitas previas.
 */
function extraerAlergiaDeLinkId(response: QuestionnaireResponse, ctx: ExtractorContext, linkId: string): void {
  if (!ctx.subject) return;
  const r = indexarRespuestas(response);
  const texto = textoDe(r, linkId);
  if (!texto || esRespuestaAlergiaNegativa(texto)) return;
  ctx.lote.crear({
    resourceType: 'AllergyIntolerance',
    patient: ctx.subject,
    clinicalStatus: CLINICAL_STATUS_ALERGIA_ACTIVA,
    code: { text: texto },
    recordedDate: new Date().toISOString(),
  });
}

export const extraerAlergias018: Extractor = (response, ctx) => extraerAlergiaDeLinkId(response, ctx, 'alergias');

/**
 * 002/003/008 — Consulta Externa / Hospitalización / Emergencia: mismo
 * patrón exacto, solo cambia Encounter.class, que ya se fijó al crear el
 * Encounter, no aquí. `diagnostico-principal`/`diagnosticos-secundarios` son
 * `open-choice` respaldados por el catálogo CIE-10 mínimo — el Condition
 * resultante lleva `code.coding` cuando el médico eligió una opción real del
 * catálogo, o solo `code.text` si tipeó algo que no matcheó ninguna.
 */
export const extraerConsultaExterna: Extractor = async (response, { medplum, subject, encounter, lote }) => {
  if (!subject) return; // sin paciente no hay a quién referenciar Condition/Observation.
  const r = indexarRespuestas(response);
  const motivo = textoDe(r, 'motivo-consulta');
  const examenFisico = textoDe(r, 'examen-fisico');
  const codigosDiagnostico = [
    ...respuestasCrudasDe(response, 'diagnostico-principal'),
    ...respuestasCrudasDe(response, 'diagnosticos-secundarios'),
  ]
    .map(codeDeRespuestaDiagnostico)
    .filter((c): c is CodeableConcept => c !== null);

  if (motivo && encounter) {
    const actual = await medplum.readReference(encounter);
    lote.actualizarEncounterAbierto({ ...actual, id: actual.id!, reasonCode: [{ text: motivo }] });
  }

  for (const code of codigosDiagnostico) {
    lote.crear({
      resourceType: 'Condition',
      subject,
      encounter,
      code,
      recordedDate: new Date().toISOString(),
    });
  }

  if (examenFisico) {
    lote.crear({
      resourceType: 'Observation',
      status: 'final',
      subject,
      encounter,
      code: { text: 'Hallazgos de examen físico' },
      valueString: examenFisico,
      effectiveDateTime: new Date().toISOString(),
    });
  }

  extraerAlergiaDeLinkId(response, { medplum, subject, encounter, lote }, 'antecedentes-alergicos');
};

/**
 * 017 — Protocolo Quirúrgico: el Questionnaire es la fuente de verdad
 * narrativa; acá se saca el `Procedure` discreto.
 */
export const extraerProtocoloQuirurgico: Extractor = (response, { subject, encounter, lote }) => {
  if (!subject) return;
  const r = indexarRespuestas(response);
  const nombreCirugia = textoDe(r, 'nombre-cirugia');
  if (!nombreCirugia) return;
  const sitioAnatomico = textoDe(r, 'sitio-anatomico');
  const tecnica = textoDe(r, 'tecnica-quirurgica');
  const complicaciones = textoDe(r, 'complicaciones');

  lote.crear({
    resourceType: 'Procedure',
    status: 'completed',
    code: { text: nombreCirugia },
    subject,
    encounter,
    performedDateTime: new Date().toISOString(),
    bodySite: sitioAnatomico ? [{ text: sitioAnatomico }] : undefined,
    note: [tecnica && `Técnica: ${tecnica}`, complicaciones && `Complicaciones: ${complicaciones}`]
      .filter((v): v is string => !!v)
      .map((text) => ({ text })),
  });
};

const EXT_DATOS_GESTACIONALES = 'http://logiacare.ec/fhir/datos-gestacionales';
// Fundal height of uterus — LOINC verificado (11881-0). La frecuencia
// cardíaca fetal no tiene un LOINC de uso tan inequívoco como los demás
// signos vitales — se deja en código local hasta verificarlo formalmente.
const ALTURA_UTERINA_LOINC = '11881-0';
const SISTEMA_CONTROL_PRENATAL = 'http://logiacare.ec/fhir/control-prenatal';

/**
 * 051 — Historia Clínica Materno Perinatal: extensión custom para datos
 * gestacionales (FUM, FPP, fórmula obstétrica G/P/A/C) + Observation por
 * cada control prenatal (altura uterina, FCF...) + Condition (factores de
 * riesgo obstétrico). Se re-envía el mismo formulario en cada visita
 * prenatal — cada envío deja un Observation nuevo.
 */
export const extraerHistoriaMaternoPerinatal: Extractor = async (response, { medplum, subject, encounter, lote }) => {
  if (!subject) return;
  const r = indexarRespuestas(response);
  const ahora = new Date().toISOString();

  const gestas = numeroDe(r, 'formula-obstetrica-gestas');
  const partos = numeroDe(r, 'formula-obstetrica-partos');
  const abortos = numeroDe(r, 'formula-obstetrica-abortos');
  const cesareas = numeroDe(r, 'formula-obstetrica-cesareas');
  const fum = textoDe(r, 'fecha-ultima-menstruacion');
  const fpp = textoDe(r, 'fecha-probable-parto');

  if (gestas !== undefined || partos !== undefined || abortos !== undefined || cesareas !== undefined || fum || fpp) {
    const paciente = await medplum.readReference(subject);
    const otrasExtensiones = (paciente.extension ?? []).filter((e) => e.url !== EXT_DATOS_GESTACIONALES);
    const subExtensiones = [
      fum && { url: 'fum', valueDate: fum },
      fpp && { url: 'fpp', valueDate: fpp },
      gestas !== undefined && { url: 'gestas', valueInteger: gestas },
      partos !== undefined && { url: 'partos', valueInteger: partos },
      abortos !== undefined && { url: 'abortos', valueInteger: abortos },
      cesareas !== undefined && { url: 'cesareas', valueInteger: cesareas },
    ].filter((e): e is { url: string; valueDate: string } | { url: string; valueInteger: number } => !!e);
    lote.actualizar<Patient & { id: string }>({
      ...(paciente as Patient & { id: string }),
      extension: [...otrasExtensiones, { url: EXT_DATOS_GESTACIONALES, extension: subExtensiones }],
    });
  }

  const alturaUterina = numeroDe(r, 'altura-uterina');
  if (alturaUterina !== undefined) {
    lote.crear({
      resourceType: 'Observation',
      status: 'final',
      code: { coding: [{ system: 'http://loinc.org', code: ALTURA_UTERINA_LOINC, display: 'Fundal height of uterus' }] },
      subject,
      encounter,
      effectiveDateTime: ahora,
      valueQuantity: { value: alturaUterina, unit: 'cm', system: 'http://unitsofmeasure.org', code: 'cm' },
    });
  }

  const fcf = numeroDe(r, 'frecuencia-cardiaca-fetal');
  if (fcf !== undefined) {
    lote.crear({
      resourceType: 'Observation',
      status: 'final',
      code: { coding: [{ system: SISTEMA_CONTROL_PRENATAL, code: 'fcf', display: 'Frecuencia cardíaca fetal' }] },
      subject,
      encounter,
      effectiveDateTime: ahora,
      valueQuantity: { value: fcf, unit: 'lpm', system: 'http://unitsofmeasure.org', code: '/min' },
    });
  }

  const dilatacion = textoDe(r, 'dilatacion-cervical');
  const posicion = textoDe(r, 'posicion-fetal');
  const contracciones = textoDe(r, 'contracciones');
  if (dilatacion || posicion || contracciones) {
    lote.crear({
      resourceType: 'Observation',
      status: 'final',
      code: { coding: [{ system: SISTEMA_CONTROL_PRENATAL, code: 'partograma', display: 'Partograma — hallazgos de trabajo de parto' }] },
      subject,
      encounter,
      effectiveDateTime: ahora,
      valueString: [dilatacion && `Dilatación: ${dilatacion}`, posicion && `Posición fetal: ${posicion}`, contracciones && `Contracciones: ${contracciones}`]
        .filter(Boolean)
        .join(' — '),
    });
  }

  const factoresRiesgo = textoDe(r, 'factores-riesgo-obstetrico');
  if (factoresRiesgo) {
    lote.crear({
      resourceType: 'Condition',
      subject,
      encounter,
      code: { text: factoresRiesgo },
      category: [{ coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-category', code: 'problem-list-item' }] }],
      recordedDate: ahora,
    });
  }
};

const SISTEMA_TANNER = 'http://logiacare.ec/fhir/estadios-tanner';

/**
 * 056A/056B — Atención Adolescente + Tanner: igual patrón que 002 +
 * Observation (estadios de Tanner, componente por carácter sexual
 * secundario). Reutiliza extraerConsultaExterna para la parte común.
 */
function crearExtractorTanner(caracteristicas: { linkId: string; codigo: string; etiqueta: string }[]): Extractor {
  return async (response, ctx) => {
    await extraerConsultaExterna(response, ctx);
    if (!ctx.subject) return;
    const r = indexarRespuestas(response);
    const componentes = caracteristicas
      .map((c) => {
        const estadio = textoDe(r, c.linkId);
        return estadio ? { code: { coding: [{ system: SISTEMA_TANNER, code: c.codigo, display: c.etiqueta }] }, valueString: estadio } : null;
      })
      .filter((c): c is NonNullable<typeof c> => c !== null);
    if (componentes.length === 0) return;
    ctx.lote.crear({
      resourceType: 'Observation',
      status: 'final',
      code: { coding: [{ system: SISTEMA_TANNER, code: 'tanner', display: 'Estadios de Tanner' }] },
      subject: ctx.subject,
      encounter: ctx.encounter,
      effectiveDateTime: new Date().toISOString(),
      component: componentes,
    });
  };
}

const extraerTannerMujer = crearExtractorTanner([
  { linkId: 'tanner-mamas', codigo: 'mamas', etiqueta: 'Desarrollo mamario' },
  { linkId: 'tanner-vello-pubico', codigo: 'vello-pubico', etiqueta: 'Vello púbico' },
]);

const extraerTannerHombre = crearExtractorTanner([
  { linkId: 'tanner-genitales', codigo: 'genitales', etiqueta: 'Desarrollo genital' },
  { linkId: 'tanner-vello-pubico', codigo: 'vello-pubico', etiqueta: 'Vello púbico' },
]);

const SISTEMA_PIEZA_DENTAL = 'http://logiacare.ec/fhir/pieza-dental-fdi';

/**
 * 033 — Odontología: QuestionnaireResponse (odontograma) + Condition
 * (diagnóstico periodontal/caries por pieza). El odontograma es un grupo
 * REPETIBLE (`repeats: true` sobre `type: group`) — cada repetición es un
 * item hermano con el mismo linkId "odontograma" en `response.item`, así
 * que se indexa cada instancia por separado con `indexarItems` (no
 * `indexarRespuestas`, que mezclaría pieza+hallazgo de todas las
 * repeticiones entre sí). "Sano" no genera Condition.
 */
export const extraerOdontologia: Extractor = (response, { subject, encounter, lote }) => {
  if (!subject) return;
  const ahora = new Date().toISOString();
  const instanciasOdontograma = (response.item ?? []).filter((item) => item.linkId === 'odontograma');

  for (const instancia of instanciasOdontograma) {
    const r = indexarItems(instancia.item);
    const pieza = textoDe(r, 'pieza-numero');
    const hallazgo = textoDe(r, 'pieza-hallazgo');
    if (!pieza || !hallazgo || hallazgo === 'Sano') continue;
    lote.crear({
      resourceType: 'Condition',
      subject,
      encounter,
      code: { text: hallazgo },
      bodySite: [{ coding: [{ system: SISTEMA_PIEZA_DENTAL, code: pieza, display: `Pieza ${pieza}` }] }],
      recordedDate: ahora,
    });
  }
};

/**
 * 059 (SNS-MSP-DNCSS-Form-059-2016, Manual de Seguridad del Paciente, AM 00000115 de 2016; no es de la HCU 2021) — Notificación de Eventos de Seguridad del Paciente: AdverseEvent
 * (recurso FHIR nativo para exactamente este caso) + QuestionnaireResponse.
 * A diferencia del resto del catálogo, no cuelga necesariamente de un
 * Encounter de consulta.
 */
export const extraerEventoSeguridad: Extractor = (response, { lote, subject }) => {
  if (!subject) return;
  const r = indexarRespuestas(response);
  const tipoEvento = textoDe(r, 'tipo-evento');
  const descripcion = textoDe(r, 'descripcion-evento');
  const fechaEvento = textoDe(r, 'fecha-evento');
  const severidad = textoDe(r, 'severidad');
  const desenlace = textoDe(r, 'desenlace');
  if (!descripcion) return;

  const CODIGOS_SEVERIDAD: Record<string, string> = { Leve: 'mild', Moderada: 'moderate', Grave: 'severe' };
  const CODIGOS_DESENLACE: Record<string, string> = {
    Resuelto: 'resolved',
    'En recuperación': 'recovering',
    'En curso': 'ongoing',
    'Resuelto con secuelas': 'resolvedWithSequelae',
    Fatal: 'fatal',
    Desconocido: 'unknown',
  };

  lote.crear({
    resourceType: 'AdverseEvent',
    actuality: tipoEvento === 'Evento potencial / casi-error (near miss)' ? 'potential' : 'actual',
    event: { text: descripcion },
    subject,
    date: fechaEvento ? new Date(fechaEvento).toISOString() : new Date().toISOString(),
    severity: severidad
      ? { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/adverse-event-severity', code: CODIGOS_SEVERIDAD[severidad] ?? severidad }] }
      : undefined,
    outcome: desenlace
      ? { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/adverse-event-outcome', code: CODIGOS_DESENLACE[desenlace] ?? desenlace }] }
      : undefined,
  });
};

/**
 * 024 y los 6 consentimientos nuevos (quirúrgico/anestesia/transfusión/
 * telesalud-canal/telesalud-lopdp/voluntad-anticipada — ver
 * lib/consentimientos.ts): arma el `Consent` con lo que el formulario ya
 * capturó (acompañante, representante legal para menores) MÁS las dos firmas
 * que el wizard agrega — `DocumentReference` del paciente (firma manuscrita
 * en canvas o escaneo) y `DocumentReference` del profesional vía Security
 * Data (mismo `PanelFirmaImpresion` del Grupo 1). Ninguno es un `Extractor`
 * genérico de `EXTRACTORES`: todos tienen su propio flujo con captura de
 * firma entre "guardar el QuestionnaireResponse" y "crear el Consent" (ver
 * MotorFormularioConsentimiento.tsx, quien la invoca).
 *
 * Puerto de packages/hce/src/hce/QuestionnaireEngine/extractors.ts, con una
 * corrección: `policyRule.text` citaba "AM 5216" (Reglamento de manejo de
 * información confidencial — RO 427, 29-ene-2015), que NO es la norma del
 * consentimiento informado. La norma correcta es AM 5316 ("Modelo de
 * Gestión de Aplicación del Consentimiento Informado en Práctica
 * Asistencial", RO 510, 22-feb-2016) — ver
 * proyecto_am5216_vs_5316_cita_incorrecta.md en memoria. Corregido acá
 * 2026-09-01 y en el original de packages/hce en el mismo cambio.
 *
 * `category`/`policyRule` dejaron de ser fijos (hallazgo de la auditoría de
 * cumplimiento: "el sistema no distingue tipo de consentimiento") — el
 * caller pasa `subtipo`/`policyRuleText` desde lib/consentimientos.ts. Se
 * mantiene el LOINC 59284-0 genérico como primer `category` (no rompe los
 * `Consent` del 024 ya creados en producción) y se agrega el subtipo real
 * como segundo `category`, en un sistema local.
 */
export interface ConstruirConsentimientoInformadoInput {
  medplum: MedplumClient;
  response: QuestionnaireResponse;
  subject: Reference<Patient>;
  /** Uno de los códigos de `subtipo` de lib/consentimientos.ts — va al segundo `category`. */
  subtipo: string;
  /** `policyRuleText` de lib/consentimientos.ts — norma (o su ausencia) que respalda este subtipo. */
  policyRuleText: string;
  /**
   * `DocumentReference` de la firma manuscrita digitalizada del paciente —
   * opcional: la vía de negativa-con-testigo (Bloque D del 024 oficial) no
   * la tiene, el paciente se negó a firmar hasta el documento de negativa
   * (ver `testigo`/`documentReferenceFirmaTestigo` abajo). Exactamente una
   * de las dos vías de firma debe venir siempre. Si el formulario marcó
   * `firma-representante`, es la firma del REPRESENTANTE LEGAL y se graba
   * como `firma-representante` (el paciente no firmó).
   */
  documentReferenceFirmaPaciente?: Reference<DocumentReference>;
  /** `DocumentReference` del PDF firmado por Security Data (o el adaptador simulado). */
  documentReferenceFirmaProfesional: Reference<DocumentReference>;
  /** Datos del testigo — solo cuando el paciente rechazó Y se negó a firmar el documento de negativa. */
  testigo?: { nombre: string; cedula: string };
  /** `DocumentReference` de la firma manuscrita digitalizada del testigo — va junto con `testigo`. */
  documentReferenceFirmaTestigo?: Reference<DocumentReference>;
}

export async function construirConsentimientoInformado(input: ConstruirConsentimientoInformadoInput) {
  const { medplum, response, subject, subtipo, policyRuleText, documentReferenceFirmaPaciente, documentReferenceFirmaProfesional, testigo, documentReferenceFirmaTestigo } = input;
  // `decisionDe` lee el ítem `decision` del 024 nuevo o, en los ya firmados y en los otros 6 consentimientos, `declaracion-general`.
  const aceptado = decisionDe(response) === 'acepta';
  const representante = representanteDe(response);
  // Solo el 024 nuevo trae la casilla `firma-representante`: si está marcada, la firma capturada es la del representante legal y el paciente
  // NO firmó (el papel deja en blanco "Firma del paciente"). Sin la casilla (otros códigos, 024 viejos) la firma sigue siendo `firma-paciente`.
  const firmaRepresentante = firmaPorRepresentante(response);

  const actores = [
    ...(representante.nombre ? [{ role: { text: 'Representante legal' }, reference: { display: textoPersona(representante) } }] : []),
    ...(testigo ? [{ role: { text: 'Testigo' }, reference: { display: `${testigo.nombre} (C.I. ${testigo.cedula})` } }] : []),
  ];
  const extRepresentante = extensionPersona(EXT_REPRESENTANTE, representante);
  const extTestigo = testigo ? extensionPersona(EXT_TESTIGO, testigo) : undefined;

  return medplum.createResource({
    resourceType: 'Consent',
    status: aceptado ? 'active' : 'rejected',
    scope: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/consentscope', code: 'treatment' }] },
    category: [
      { coding: [{ system: 'http://loinc.org', code: '59284-0', display: 'Consentimiento informado' }] },
      { coding: [{ system: SISTEMA_SUBTIPO_CONSENTIMIENTO, code: subtipo }] },
    ],
    // Invariante FHIR ppc-1: Consent exige `policy` o `policyRule`. No hay un
    // código formal de CodeSystem para esto — se referencia la norma con
    // texto, mismo criterio que el resto del proyecto para CodeableConcept
    // sin catálogo verificado (ver `role: { text: ... }` más abajo).
    policyRule: { text: policyRuleText },
    patient: subject,
    // Fecha y hora de la información (bloque C) o de la negativa (bloque D) que capturó el formulario; sin ellas, el instante de guardar.
    dateTime: instanteDeLaDecision(response, new Date()),
    sourceReference: documentReferenceFirmaProfesional,
    extension: [
      ...(documentReferenceFirmaPaciente
        ? [{ url: firmaRepresentante ? EXT_FIRMA_REPRESENTANTE : EXT_FIRMA_PACIENTE, valueReference: documentReferenceFirmaPaciente }]
        : []),
      ...(documentReferenceFirmaTestigo ? [{ url: EXT_FIRMA_TESTIGO, valueReference: documentReferenceFirmaTestigo }] : []),
      ...(extRepresentante ? [extRepresentante] : []),
      ...(extTestigo ? [extTestigo] : []),
    ],
    ...(actores.length > 0 ? { provision: { actor: actores } } : {}),
  });
}

export const EXTRACTORES: Record<string, Extractor> = {
  '002': extraerConsultaExterna,
  '003': extraerConsultaExterna,
  // 024 no está acá a propósito — tiene su propio flujo con captura de firma
  // (Grupo 2 del Módulo H, ver construirConsentimientoInformado más arriba,
  // invocada desde MotorFormularioConsentimiento.tsx en vez de EXTRACTORES).
  // 017 = Protocolo Quirúrgico (`nombre-cirugia` → Procedure); 018 = Pre Anestésico (`alergias` → AllergyIntolerance).
  // Estaban cruzados desde la corrección de la numeración: 017 buscaba `alergias` y 018 `nombre-cirugia`, así que ninguno
  // de los dos sacaba su recurso y no fallaba con error.
  '017': extraerProtocoloQuirurgico,
  '018': extraerAlergias018,
  // Pediatría — 028A/028B/028C pasaron a Patrón B (Formulario028A.tsx/
  // Formulario028B.tsx/Formulario028C.tsx, Serie 028 Fase 2, ver PLAN.md) —
  // ya no pasan por acá, los Questionnaire legados quedan inalcanzables por
  // el routing pero no se tocan (histórico). "029"/"028D" retirados del
  // catálogo: códigos inventados, no existen en la fuente primaria oficial.
  // 008 — Emergencia pasó a Patrón B (Formulario008.tsx, Fase 3, ver
  // PLAN.md) — ya no pasa por acá, el Questionnaire legado (008.json) queda
  // inalcanzable por el routing pero no se toca (histórico). 002/003 siguen
  // en Patrón A, sin cambios.
  // 051 — Materno Perinatal pasó a Patrón B (Formulario051.tsx, Fase 4, ver
  // PLAN.md) — ya no pasa por acá, el Questionnaire legado (051.json) y
  // `extraerHistoriaMaternoPerinatal` quedan inalcanzables por el routing
  // pero no se tocan (histórico).
  '056': extraerConsultaExterna,
  '056A': extraerTannerMujer,
  '056B': extraerTannerHombre,
  '057': extraerConsultaExterna,
  '033': extraerOdontologia,
  '121': extraerConsultaExterna,
  '059': extraerEventoSeguridad,
  // 057A (Escalas Geriátricas) no está acá a propósito: "demasiado
  // específicas para forzarlas a Observation discretas sin perder la escala
  // compuesta oficial", el QuestionnaireResponse ya es la fuente de verdad
  // completa, mismo criterio que 051.
};
